import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { MapPin, Calendar as CalIcon, Users, Check, Bell, Sparkles } from "lucide-react";
import { toast } from "sonner";



function Countdown({ to }) {
  const { t } = useLang();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(id); }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return (
    <div className="flex gap-4 text-white">
      {[[days, t("events.days")], [hours, t("events.hours")], [mins, t("events.minutes")]].map(([v, l], i) => (
        <div key={i} className="text-center">
          <div className="font-display text-3xl md:text-5xl font-bold">{v}</div>
          <div className="text-xs uppercase tracking-widest opacity-80">{l}</div>
        </div>
      ))}
    </div>
  );
}

const EOY_START = "2026-11-29T18:00:00-05:00";

function EoyCountdown({ to }) {
  const { lang } = useLang();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) {
    return <div className="font-display text-2xl font-bold" data-testid="eoy-countdown-live">{lang === "fr" ? "La fête a commencé !" : "The celebration has begun!"}</div>;
  }
  const units = [
    [Math.floor(ms / 86400000), lang === "fr" ? "Jours" : "Days"],
    [Math.floor((ms % 86400000) / 3600000), lang === "fr" ? "Heures" : "Hours"],
    [Math.floor((ms % 3600000) / 60000), lang === "fr" ? "Min" : "Mins"],
    [Math.floor((ms % 60000) / 1000), lang === "fr" ? "Sec" : "Secs"],
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {units.map(([v, l]) => (
        <div key={l} className="text-center bg-white/20 border border-white/30 rounded-lg py-2" data-testid={`eoy-countdown-${l.toLowerCase()}`}>
          <div className="font-display text-2xl md:text-3xl font-bold tabular-nums">{String(v).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-widest opacity-90">{l}</div>
        </div>
      ))}
    </div>
  );
}


export default function Events() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upcoming = useMemo(() => events.filter((e) => new Date(e.start_at) > new Date()), [events]);
  const next = upcoming[0];

  const rsvp = async (id) => {
    if (!user) { toast.error(t("events.rsvpLogin")); return; }
    try {
      await api.post(`/events/${id}/rsvp`);
      toast.success(t("events.rsvpd"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="events-page">
      <section className="cupe-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold">{t("events.title")}</h1>
          <p className="text-lg text-white/90 mt-3 max-w-2xl">{t("events.sub")}</p>

          {next && (
            <div className="mt-10 bg-white/15 backdrop-blur border border-white/30 rounded-2xl p-6 md:p-8 max-w-3xl">
              <div className="text-xs uppercase tracking-widest font-semibold opacity-90 mb-2">{t("events.countdown")}</div>
              <div className="font-display text-xl md:text-2xl font-bold mb-4">{lang === "fr" ? next.title_fr : next.title_en}</div>
              <Countdown to={next.start_at} />
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" data-testid="eoy-celebration-notice">
        <div className="relative bg-white border-2 border-amber-400 rounded-2xl shadow-xl overflow-hidden">
          <div className="absolute -top-3 -left-3 z-10">
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60 animate-ping" />
              <span className="relative inline-flex items-center justify-center bg-gradient-to-br from-amber-400 to-pink-500 text-white rounded-full h-14 w-14 shadow-lg border-4 border-white">
                <Sparkles size={26} className="animate-[wiggle_1s_ease-in-out_infinite]" />
              </span>
            </span>
          </div>

          <div className="bg-gradient-to-r from-amber-400 via-pink-500 to-rose-500 text-white px-6 md:px-10 py-3 pl-20 md:pl-24 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 bg-white/25 border border-white/40 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              {lang === "fr" ? "Ne manquez pas" : "Save The Date"}
            </span>
            <span className="font-display font-bold text-lg md:text-xl">
              {lang === "fr" ? "Célébration de fin d'année 2026" : "End-of-Year Celebration 2026"}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-0">
            <a
              href="https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/2a7ca11a83cbb6e9_IMG-20261103-WA0036.jpg"
              target="_blank"
              rel="noreferrer"
              className="flex items-center bg-slate-50"
              data-testid="eoy-poster-link"
            >
              <div className="relative w-full" style={{ containerType: "inline-size" }} data-testid="eoy-poster-wrapper">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/2a7ca11a83cbb6e9_IMG-20261103-WA0036.jpg"
                  alt={lang === "fr" ? "Affiche - Célébration de fin d'année 2026, CUPE Local 4957" : "End-of-Year Celebration 2026 poster, CUPE Local 4957"}
                  className="block w-full h-auto"
                />
              </div>
            </a>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="text-xs uppercase font-bold tracking-widest text-pink-600">
                {lang === "fr" ? "Célébrer, apprécier, unir" : "Celebrate · Appreciate · Unite"}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                {lang === "fr" ? "Célébration de fin d'année 2026" : "End-of-Year Celebration 2026"}
              </h3>
              <div className="mt-5 space-y-3 text-slate-700">
                <div className="flex items-start gap-3">
                  <CalIcon size={18} className="text-pink-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Date" : "Date"}</div>
                    <div className="font-semibold">{lang === "fr" ? "Dimanche 29 novembre 2026" : "Sunday, November 29, 2026"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-pink-600 mt-0.5 font-bold">◷</span>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Heure" : "Time"}</div>
                    <div className="font-semibold">6:00 PM - 12:00 Midnight</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-pink-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Lieu" : "Venue"}</div>
                    <div className="font-semibold">Holiday Inn</div>
                    <div className="text-sm text-slate-600">1199 Joseph Cyr St, Ottawa</div>
                  </div>
                </div>
              </div>
              <div className="mt-5 rounded-lg bg-gradient-to-r from-amber-50 to-pink-50 border border-amber-200 px-4 py-3 text-sm text-slate-700">
                <span className="font-bold text-pink-700">{lang === "fr" ? "Tenue : " : "Dress code: "}</span>
                {lang === "fr" ? "Tenue de cocktail / Vêtements traditionnels - Jeans strictement interdits." : "Cocktail Attire / Traditional Wear - Strictly No Jeans."}
              </div>
              <div className="mt-5 rounded-xl bg-gradient-to-r from-amber-400 via-pink-500 to-rose-500 px-5 py-4 text-white shadow-md" data-testid="eoy-countdown">
                <div className="text-xs uppercase font-bold tracking-widest opacity-90 mb-2">
                  {lang === "fr" ? "Compte à rebours de la fête" : "Countdown to the celebration"}
                </div>
                <EoyCountdown to={EOY_START} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-testid="ratification-notice">
        <div className="relative bg-white border-2 border-[#d81b7a] rounded-2xl shadow-xl overflow-hidden">
          <div className="absolute -top-3 -left-3 z-10">
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#d81b7a] opacity-60 animate-ping" />
              <span className="relative inline-flex items-center justify-center bg-[#d81b7a] text-white rounded-full h-14 w-14 shadow-lg border-4 border-white">
                <Bell size={26} className="animate-[wiggle_1s_ease-in-out_infinite]" />
              </span>
            </span>
          </div>

          <div className="cupe-gradient text-white px-6 md:px-10 py-3 pl-20 md:pl-24 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 bg-white/20 border border-white/40 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              {lang === "fr" ? "Avis important" : "Important Notice"}
            </span>
            <span className="font-display font-bold text-lg md:text-xl">
              {lang === "fr" ? "Réunion de ratification - Marquez votre calendrier" : "Ratification Meeting - Mark Your Calendar"}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-0">
            <a
              href="https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/iyvwfjuo_Screenshot_20260924_213912_Write%20on%20PDF%20%281%29.jpg"
              target="_blank"
              rel="noreferrer"
              className="block bg-slate-50"
              data-testid="ratification-poster-link"
            >
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/iyvwfjuo_Screenshot_20260924_213912_Write%20on%20PDF%20%281%29.jpg"
                alt={lang === "fr" ? "Affiche - Réunion de ratification" : "Ratification Meeting poster"}
                className="w-full h-full object-contain"
              />
            </a>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="text-xs uppercase font-bold tracking-widest text-[#d81b7a]">
                {lang === "fr" ? "Votre voix compte" : "Your Voice Matters"}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                {lang === "fr" ? "Réunion de ratification" : "Ratification Meeting"}
              </h3>
              <div className="mt-5 space-y-3 text-slate-700">
                <div className="flex items-start gap-3">
                  <CalIcon size={18} className="text-[#d81b7a] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Date" : "Date"}</div>
                    <div className="font-semibold">{lang === "fr" ? "Jeudi 1er octobre 2026" : "Thursday, October 1, 2026"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#d81b7a] mt-0.5 font-bold">◷</span>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Heure" : "Time"}</div>
                    <div className="font-semibold">7:00 a.m. - 4:00 p.m.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#d81b7a] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{lang === "fr" ? "Lieu" : "Location"}</div>
                    <div className="font-semibold">{lang === "fr" ? "Salle du personnel Oakpark" : "Oakpark Staff Room"}</div>
                  </div>
                </div>
              </div>
              <div className="mt-5 rounded-lg bg-pink-50 border border-pink-200 px-4 py-3 text-sm text-slate-700">
                {lang === "fr" ? "Des collations seront disponibles tout au long de la journée." : "Snacks will be available throughout the day."}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-4">
          {upcoming.map((e) => {
            const d = new Date(e.start_at);
            const joined = user && e.rsvps?.includes(user.id);
            return (
              <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="event-row">
                <div className="text-center cupe-gradient text-white rounded-xl p-4 w-24 flex-shrink-0">
                  <div className="text-xs uppercase">{d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { month: "short" })}</div>
                  <div className="font-display font-bold text-3xl leading-none mt-1">{d.getDate()}</div>
                  <div className="text-xs mt-1 opacity-90">{d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="flex-1">
                  <div className="font-display font-semibold text-slate-900 text-lg">{lang === "fr" ? e.title_fr : e.title_en}</div>
                  <p className="text-slate-600 text-sm mt-1">{lang === "fr" ? e.description_fr : e.description_en}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin size={14} />{e.location}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={14} />{e.rsvps?.length || 0} RSVP</span>
                  </div>
                </div>
                <button
                  onClick={() => rsvp(e.id)}
                  disabled={joined}
                  className={`px-5 py-3 rounded-md font-semibold min-h-[48px] ${joined ? "bg-emerald-100 text-emerald-700" : "cupe-gradient text-white hover:opacity-95"}`}
                  data-testid={`rsvp-btn-${e.id}`}
                >
                  {joined ? <span className="inline-flex items-center gap-1"><Check size={16} /> {t("events.rsvpd")}</span> : t("events.rsvp")}
                </button>
              </div>
            );
          })}
          {upcoming.length === 0 && <p className="text-slate-400">{t("events.noEvents")}</p>}
        </div>
      </section>
    </div>
  );
}
